import { FormikProps, getIn } from 'formik';
import cc from 'classcat';
import { useState, ChangeEvent } from 'react';
import { Label } from '../Label';

interface InputFormikProps<T> {
    id: string;
    type?: string;
    placeholder?: string;
    label?: string;
    isRequired?: boolean;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    className?: string;
    maxLength?: number;
    isDisabled?: boolean;
    iconSrc?: string;
    iconAlt?: string;
    iconOnClick?: () => void;
    context: FormikProps<T>;
    rightAlignErrorMsg?: boolean;
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
    onClick?: React.MouseEventHandler<HTMLInputElement>;
    value?: string;
    labelInfoText?: string;
}

export const InputFormik = <T extends object>({
    id,
    type,
    placeholder,
    label,
    isRequired,
    onChange,
    className,
    maxLength,
    isDisabled,
    iconSrc,
    iconAlt,
    iconOnClick,
    context,
    rightAlignErrorMsg,
    onBlur,
    onKeyDown,
    onClick,
    value,
    labelInfoText,
}: InputFormikProps<T>) => {
    const { values, handleChange, errors, touched } = context;

    const [isFocused, setIsFocused] = useState<boolean>(false);
    const isError = () => {
        if (id.includes('.')) {
            return getIn(touched, id) && getIn(errors, id);
        }
        return touched[id as keyof T] && errors[id as keyof T];
    };

    return (
        <div className={cc(['input-style', className])}>
            {label && labelInfoText ? (
                <label htmlFor={id} className={cc(['input-style__label', 'input-style__label--with-info', 'paragraph-4'])}>
                    {label}
                    {isRequired ? <span className="input-style_is_req">*</span> : null}
                    <span className="input-style__label-info-wrapper">
                        <img className="input-style__label-info-wrapper--icon" src="/svg/slider/info-icon.svg" alt="" />
                        <span className="paragraph-5 input-style__label-info-wrapper--tooltip" role="tooltip">
                            {labelInfoText}
                        </span>
                    </span>
                </label>
            ) : (
                label && <Label label={label} isRequired={isRequired} className="input-style__label paragraph-4" />
            )}
            <div className="input-style__field-wrapper">
                <input
                    className={cc(['input-style__field', { 'input-style__field--error': isError() }])}
                    id={id}
                    name={id}
                    value={value !== undefined ? value : id.includes('.') ? getIn(values, id) : (values[id as keyof T] as string)}
                    type={type ?? 'text'}
                    placeholder={placeholder}
                    onFocus={() => setIsFocused(true)}
                    onBlur={e => {
                        setIsFocused(false);
                        onBlur?.(e);
                    }}
                    onChange={onChange ?? handleChange}
                    disabled={isDisabled}
                    maxLength={maxLength}
                    onKeyDown={onKeyDown}
                    onClick={onClick}
                    onWheel={e => e.currentTarget.blur()}
                />
                {iconSrc && (
                    <button type="button" className={cc(['input-style__icon-button', { 'input-style__icon-button--disabled': isDisabled }])}>
                        <img src={iconSrc} alt={iconAlt} onClick={iconOnClick} />
                    </button>
                )}
            </div>
            {isError() && !isFocused && (
                <span className={cc(['input-style__error-msg', rightAlignErrorMsg && 'input-style__error-msg--right'])}>
                    {id.includes('.') ? getIn(errors, id) : (errors[id as keyof T] as string)}
                </span>
            )}
        </div>
    );
};
